import { useQuery, useMutation, useQueryClient } from 'react-query';
import { supabase } from '../lib/supabaseClient';
import { Market, MarketSearch } from '../types/market';
import { useAuth } from '../components/auth/AuthContext';

export class MarketError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'MarketError';
  }
}

export const useMarkets = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: markets = [], isLoading: isLoadingMarkets } = useQuery<Market[]>(
    'markets',
    async () => {
      const { data, error } = await supabase
        .from('markets')
        .select('*')
        .order('order');

      if (error) {
        console.error('Error fetching markets:', error);
        throw new MarketError('Failed to fetch markets');
      }
      return data;
    }
  );

  const { data: marketSearches = [], isLoading: isLoadingSearches } = useQuery<MarketSearch[]>(
    ['marketSearches', user?.email],
    async () => {
      const { data: searches, error: searchError } = await supabase
        .from('market_searches')
        .select(`
          id,
          market_id,
          search:search_id (
            id,
            query,
            searches,
            likes
          )
        `);

      if (searchError) {
        console.error('Error fetching market searches:', searchError);
        throw new MarketError('Failed to fetch market searches');
      }
      
      return searches.map(item => ({
        id: item.id,
        marketId: item.market_id,
        search: {
          id: item.search.id,
          query: item.search.query,
          searches: item.search.searches,
          likes: item.search.likes
        }
      }));
    },
    {
      enabled: true,
      staleTime: 30000,
      cacheTime: 5 * 60 * 1000
    }
  );

  // Create market
  const { mutateAsync: createMarket } = useMutation(
    async (data: Omit<Market, 'id'>) => {
      const { error } = await supabase
        .from('markets')
        .insert(data);

      if (error) throw error;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('markets');
      }
    }
  );

  // Update market
  const { mutateAsync: updateMarket } = useMutation(
    async (data: Market) => {
      const { error } = await supabase
        .from('markets')
        .update(data)
        .eq('id', data.id);

      if (error) throw error;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('markets');
      }
    }
  );

  // Delete market
  const { mutateAsync: deleteMarket } = useMutation(
    async (id: string) => {
      const { error } = await supabase
        .from('markets')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('markets');
      }
    }
  );

  // Create market search
  const { mutateAsync: createMarketSearch } = useMutation(
    async ({ marketId, query }: { marketId: string; query: string }) => {
      // First create or get the search
      const { data: search, error: searchError } = await supabase
        .from('searches')
        .select()
        .eq('query', query)
        .maybeSingle();

      if (searchError) throw searchError;

      let searchId;
      if (search) {
        searchId = search.id;
      } else {
        const { data: newSearch, error: createError } = await supabase
          .from('searches')
          .insert({ query })
          .select()
          .single();

        if (createError) throw createError;
        searchId = newSearch.id;
      }

      // Then create the market search connection
      const { error: linkError } = await supabase
        .from('market_searches')
        .insert({
          market_id: marketId,
          search_id: searchId
        });

      if (linkError) throw linkError;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['marketSearches', user?.email]);
      }
    }
  );

  // Delete market search
  const { mutateAsync: deleteMarketSearch } = useMutation(
    async (marketSearchId: string) => {
      const { error } = await supabase
        .from('market_searches')
        .delete()
        .eq('id', marketSearchId);

      if (error) throw error;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['marketSearches', user?.email]);
      }
    }
  );

  return {
    markets,
    marketSearches,
    isLoading: isLoadingMarkets || isLoadingSearches,
    createMarket,
    updateMarket,
    deleteMarket,
    createMarketSearch,
    deleteMarketSearch
  };
};